import React, { useState } from "react";

import {
  Box,
  Button,
  IconButton,
  InputBase,
  Stack,
  Tooltip,
  Typography,
} from "@mui/material";
import KeyboardDoubleArrowLeftIcon from "@mui/icons-material/KeyboardDoubleArrowLeft";
import KeyboardDoubleArrowRightIcon from "@mui/icons-material/KeyboardDoubleArrowRight";

import { formatTimecode, parseTimecode } from "../utils";

interface EdgeControlProps {
  label: string;
  shortcut: string;
  value: number;
  onChange: (time: number) => void;
  onSetToPlayhead: () => void;
  onJump: () => void;
  onStepWord?: (direction: -1 | 1) => void;
}

const nudgeButtonSx = {
  minWidth: 0,
  px: 0.75,
  fontSize: 12,
  fontVariantNumeric: "tabular-nums",
};

export default function EdgeControl({
  label,
  shortcut,
  value,
  onChange,
  onSetToPlayhead,
  onJump,
  onStepWord,
}: EdgeControlProps) {
  const [draft, setDraft] = useState<string | null>(null);
  const [isInvalid, setIsInvalid] = useState(false);

  const commitDraft = () => {
    if (draft === null) return;
    const parsed = parseTimecode(draft);
    if (parsed === null) {
      setIsInvalid(true);
      return;
    }
    onChange(parsed);
    setDraft(null);
    setIsInvalid(false);
  };

  return (
    <Box
      sx={{
        p: 1.25,
        border: 1,
        borderColor: "grey.200",
        borderRadius: 1.5,
        bgcolor: "background.paper",
      }}
    >
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        sx={{ mb: 0.75 }}
      >
        <Tooltip title={`Jump to ${label.toLowerCase()}`}>
          <Button
            size="small"
            onClick={onJump}
            sx={{
              minWidth: 0,
              p: 0,
              fontWeight: 600,
              color: "text.primary",
              textTransform: "none",
            }}
          >
            {label}
          </Button>
        </Tooltip>
        <Tooltip title={`Press ${shortcut}`}>
          <Button
            size="small"
            variant="text"
            onClick={onSetToPlayhead}
            sx={{ textTransform: "none", fontSize: 12, py: 0 }}
          >
            Set to playhead
            <Typography
              component="kbd"
              sx={{
                ml: 0.75,
                px: 0.5,
                fontSize: 10,
                border: 1,
                borderColor: "grey.300",
                borderRadius: 0.5,
                color: "text.secondary",
              }}
            >
              {shortcut}
            </Typography>
          </Button>
        </Tooltip>
      </Stack>

      <Stack direction="row" alignItems="center" spacing={0.25}>
        {onStepWord && (
          <Tooltip title="Previous word boundary">
            <IconButton size="small" onClick={() => onStepWord(-1)}>
              <KeyboardDoubleArrowLeftIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        )}
        <Button
          size="small"
          sx={nudgeButtonSx}
          onClick={() => onChange(value - 1)}
        >
          −1s
        </Button>
        <Button
          size="small"
          sx={nudgeButtonSx}
          onClick={() => onChange(value - 0.1)}
        >
          −.1
        </Button>
        <InputBase
          value={draft ?? formatTimecode(value)}
          onFocus={(e) => {
            setDraft(formatTimecode(value));
            e.target.select();
          }}
          onChange={(e) => {
            setDraft(e.target.value);
            setIsInvalid(false);
          }}
          onBlur={() => {
            commitDraft();
            setDraft(null);
            setIsInvalid(false);
          }}
          onKeyDown={(e) => {
            if (e.key === "Enter") commitDraft();
            if (e.key === "Escape") {
              setDraft(null);
              setIsInvalid(false);
              (e.target as HTMLInputElement).blur();
            }
          }}
          inputProps={{
            "aria-label": `${label} time`,
            style: { textAlign: "center" },
          }}
          sx={{
            flex: 1,
            minWidth: 84,
            px: 0.5,
            border: 1,
            borderColor: isInvalid ? "error.main" : "grey.300",
            borderRadius: 1,
            fontSize: 15,
            fontWeight: 600,
            fontVariantNumeric: "tabular-nums",
          }}
        />
        <Button
          size="small"
          sx={nudgeButtonSx}
          onClick={() => onChange(value + 0.1)}
        >
          +.1
        </Button>
        <Button
          size="small"
          sx={nudgeButtonSx}
          onClick={() => onChange(value + 1)}
        >
          +1s
        </Button>
        {onStepWord && (
          <Tooltip title="Next word boundary">
            <IconButton size="small" onClick={() => onStepWord(1)}>
              <KeyboardDoubleArrowRightIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        )}
      </Stack>
    </Box>
  );
}
